import type { Administrator } from "../api/types";
import { formatDateTime } from "../components/ui";
import { rolesLabel } from "./roles";
import type { AccountAction } from "./AccountActionDialog";

/**
 * Who can use this dashboard, and what each of them may do.
 *
 * A DISABLED ACCOUNT IS A ROW LIKE ANY OTHER, marked Disabled. Hiding it would
 * make the list a list of people who can sign in rather than a list of
 * administrators, and an account nobody can see is an account nobody reviews.
 *
 * "Signed in" is a count of LIVE sessions, which is what an owner wants to know
 * before disabling somebody. Nothing about a session itself is shown — no
 * token, no address, no browser — because none of it is any of this screen's
 * business.
 */
export function UsersTable({
  items,
  currentEmail,
  canManage,
  onRoles,
  onAction,
}: {
  items: Administrator[];
  currentEmail: string | null;
  canManage: boolean;
  onRoles: (administrator: Administrator) => void;
  onAction: (administrator: Administrator, action: AccountAction) => void;
}) {
  return (
    <table className="table users__table">
      <thead>
        <tr>
          <th scope="col">User</th>
          <th scope="col">Email</th>
          <th scope="col">Status</th>
          <th scope="col">Roles</th>
          <th scope="col">Last login</th>
          <th scope="col">Sessions</th>
          <th scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map((administrator) => {
          const isSelf =
            currentEmail !== null && administrator.email === currentEmail;
          return (
            <tr key={administrator.admin_id} data-testid="user-row">
              <td>
                <span className="users__name">
                  {administrator.display_name}
                  {isSelf ? <span className="badge">you</span> : null}
                </span>
              </td>
              <td className="muted">{administrator.email}</td>
              <td>
                <span
                  className={
                    administrator.is_active
                      ? "badge badge--published"
                      : "badge badge--archived"
                  }
                >
                  {administrator.is_active ? "Active" : "Disabled"}
                </span>
                {administrator.must_change_password ? (
                  <span className="badge" title="Still using a temporary password">
                    temporary password
                  </span>
                ) : null}
              </td>
              <td>{rolesLabel(administrator.roles)}</td>
              <td className="muted">
                {formatDateTime(administrator.last_login_at)}
              </td>
              <td className="num">{administrator.active_session_count}</td>
              <td>
                {canManage ? (
                  <div className="row">
                    {/* Disabled on your own row, like the other two. An
                        administrator may not change their own authority — the
                        backend refuses it whatever this table offers — and a
                        control whose only possible answer is a refusal is one
                        an operator should never be invited to press. The row
                        already shows the roles this account holds, so nothing
                        is hidden by taking the dialog away. */}
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={isSelf}
                      title={
                        isSelf ? "You cannot change your own roles" : undefined
                      }
                      onClick={() => onRoles(administrator)}
                    >
                      Roles
                    </button>
                    {administrator.is_active ? (
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={isSelf}
                        title={
                          isSelf ? "You cannot disable your own account" : undefined
                        }
                        onClick={() => onAction(administrator, "disable")}
                      >
                        Disable
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => onAction(administrator, "reactivate")}
                      >
                        Reactivate
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={isSelf}
                      title={
                        isSelf
                          ? "Change your own password from the header"
                          : undefined
                      }
                      onClick={() => onAction(administrator, "reset")}
                    >
                      Reset password
                    </button>
                  </div>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
